import { invalidCouponMessage, isInvalidReason } from "@/lib/discount-evaluation/coupon-copy"
import type { CouponEvaluation } from "@/lib/discount-evaluation/types"
import {
  couponCodeKey,
  dropPendingCouponCode,
  getPendingCouponCodes,
  keepCouponForLater,
  savePendingCouponCodes,
} from "@/lib/pending-coupon"

/** What happens to the coupons already on the order when another one arrives: `replace` keeps only the newest, `combine` keeps them all. */
export type CouponArrivalDecision = "replace" | "combine"

const NOT_TOGETHER = new Set(["EVALUATION_REASON_NOT_COMBINABLE", "EVALUATION_REASON_MAX_COUPONS_EXCEEDED"])

/**
 * Whether a newly arrived code may ride with the ones already on the order,
 * by coupon-service's verdict for all of them on this bag: together only when
 * the merchant accepts that many coupons and none came back as not
 * combinable. Without a verdict (empty bag, nothing could be asked) the
 * newest replaces the others.
 */
export function decideCouponArrival(code: string, existing: readonly string[], evaluation: CouponEvaluation | null): CouponArrivalDecision {
  if (existing.length === 0) return "combine"
  if (!evaluation || evaluation.maxCouponsPerOrder < existing.length + 1) return "replace"
  const involved = new Set([code, ...existing].map(couponCodeKey))
  const clash = evaluation.rejected.some((rejected) => NOT_TOGETHER.has(rejected.reason) && involved.has(couponCodeKey(rejected.code)))
  return clash ? "replace" : "combine"
}

/** Leaves only `code` applied: the replaced ones leave the order but stay in the customer's wallet. */
export function replaceStashedCoupons(merchantId: string, code: string, replaced: readonly string[]): void {
  for (const old of replaced) dropPendingCouponCode(old, merchantId)
  const pending = getPendingCouponCodes(merchantId)
  if (!pending.some((entry) => couponCodeKey(entry) === couponCodeKey(code))) savePendingCouponCodes([...pending, code], merchantId)
}

/** Undoes {@link replaceStashedCoupons}: the newest code leaves the order (it stays in the wallet) and the replaced ones are applied again. */
export function undoCouponReplacement(merchantId: string, code: string, replaced: readonly string[]): void {
  dropPendingCouponCode(code, merchantId)
  const pending = getPendingCouponCodes(merchantId)
  savePendingCouponCodes([...pending, ...replaced.filter((old) => !pending.includes(old))], merchantId)
  for (const old of replaced) keepCouponForLater(old, merchantId)
}

/**
 * What a verdict says about `codes` beyond the bag: `refused` will never work
 * for this customer (not theirs, expired, used up…) and leave the wallet too;
 * `outranked` lost to a better combination and only leave the order. A code
 * only outside its weekly window is in neither.
 */
export function unusableCouponCodes(evaluation: CouponEvaluation, codes: readonly string[]): { refused: string[]; outranked: string[] } {
  const wanted = new Map(codes.map((code) => [couponCodeKey(code), code]))
  const result: { refused: string[]; outranked: string[] } = { refused: [], outranked: [] }
  for (const rejected of evaluation.rejected) {
    const code = wanted.get(couponCodeKey(rejected.code))
    if (!code || rejected.reason === "EVALUATION_REASON_OUTSIDE_AVAILABILITY") continue
    if (NOT_TOGETHER.has(rejected.reason)) result.outranked.push(code)
    else if (isInvalidReason(rejected.reason)) result.refused.push(code)
  }
  return result
}

/** The one toast line for codes taken off the order on their own. */
export function removedCouponsMessage(codes: readonly string[]): string {
  if (codes.length === 1) return `O cupom ${codes[0]} não vale para a sua conta e foi removido.`
  return `Os cupons ${codes.slice(0, -1).join(", ")} e ${codes[codes.length - 1]} não valem para a sua conta e foram removidos.`
}

/** The toast lines for refused codes: another account's coupon keeps the removed line, any other reason says why the code is invalid. */
export function refusedCouponsMessages(evaluation: CouponEvaluation, refused: readonly string[]): string[] {
  const reasons = new Map(evaluation.rejected.map((rejected) => [couponCodeKey(rejected.code), rejected.reason]))
  const removed: string[] = []
  const messages: string[] = []
  for (const code of refused) {
    const reason = reasons.get(couponCodeKey(code))
    if (!reason || reason === "EVALUATION_REASON_NOT_ASSIGNED_TO_CUSTOMER") removed.push(code)
    else messages.push(invalidCouponMessage(code, reason))
  }
  return removed.length > 0 ? [...messages, removedCouponsMessage(removed)] : messages
}
